import { BarChart2, Globe, ShieldCheck, Sprout } from "lucide-react";
import { useMemo } from "react";
import { Button, Separator } from "randmarcomps";
import { Reseller as ApiReseller } from "randmar-api-client";
import LegacyReseller from "../../stores/reseller/Reseller";
import DownloadSalesReport from "../manufacturer/DownloadSalesReport";
import AddResellerToOpportunityWrapper from "./AddResellerToOpportunityWrapper";
import ManageQualificationWrapper from "./ManageQualificationWrapper";

interface IProps {
  reseller: LegacyReseller | ApiReseller;
}

const isLegacy = (r: LegacyReseller | ApiReseller): r is LegacyReseller => "arEmail" in r;

const toApiReseller = (reseller: LegacyReseller | ApiReseller): ApiReseller => {
  if (!isLegacy(reseller)) {
    return reseller;
  }
  return {
    Address1: reseller.address1,
    Address2: reseller.address2,
    City: reseller.city,
    ContactName: reseller.contactName,
    CreationDate: reseller.createdDate,
    CreditLimit: reseller.creditLimit,
    DefaultWarehouse: reseller.defaultWarehouse,
    POEmail: reseller.pOEmail,
    PublicEmail: reseller.publicEmail,
    Website: reseller.website,
    Qualifications: reseller.isQualified ? [] : null,
    ResellerId: reseller.resellerId,
    Name: reseller.name,
    Phone: reseller.phone,
    PostalCode: reseller.postalCode,
    Province: reseller.province,
    Country: reseller.country,
  };
};

const ResellerActions = ({ reseller: rawReseller }: IProps) => {
  const reseller = useMemo(() => toApiReseller(rawReseller), [rawReseller]);
  const websiteUrl = reseller.Website
    ? reseller.Website.startsWith("http")
      ? reseller.Website
      : `//${reseller.Website}`
    : null;

  return (
    <div className="w-full max-w-[280px] p-2">
      <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Reseller</p>

      {websiteUrl ? (
        <Button asChild variant="ghost" className="h-10 w-full justify-start gap-2 rounded-xl px-3 text-[15px] font-medium text-slate-900">
          <a href={websiteUrl} target="_blank" rel="noopener noreferrer">
            <Globe className="h-4 w-4" aria-hidden="true" />
            Website
          </a>
        </Button>
      ) : null}

      <DownloadSalesReport
        reseller={rawReseller}
        trigger={(
          <Button variant="ghost" className="h-10 w-full justify-start gap-2 rounded-xl px-3 text-[15px] font-medium text-slate-900">
            <BarChart2 className="h-4 w-4" aria-hidden="true" />
            Sales report
          </Button>
        )}
      />

      <Separator className="my-2 bg-gray-100" />

      <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Qualification</p>

      <ManageQualificationWrapper reseller={rawReseller}>
        <Button variant="ghost" className="h-10 w-full justify-start gap-2 rounded-xl px-3 text-[15px] font-medium text-slate-900">
          <ShieldCheck className="h-4 w-4" aria-hidden="true" />
          Qualification status
        </Button>
      </ManageQualificationWrapper>

      <AddResellerToOpportunityWrapper reseller={rawReseller}>
        <Button variant="ghost" className="h-10 w-full justify-start gap-2 rounded-xl px-3 text-[15px] font-medium text-slate-900">
          <Sprout className="h-4 w-4" aria-hidden="true" />
          Add to opportunity
        </Button>
      </AddResellerToOpportunityWrapper>
    </div>
  );
};

export default ResellerActions;
